/**
 * Presentation-only chat transcript shared by the member and admin applications.
 *
 * The wire models intentionally stay in each app. This small view model is the stable
 * seam that lets either side render the same conversational bubbles without coupling
 * the design system to an API or to React Query.
 */

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useRef } from "react";

import { RADIUS } from "../../theme/tokens";

export interface ChatMessageView {
  id: string;
  body: string;
  createdAt: string;
  isOwn: boolean;
  senderLabel?: string | null;
}

export interface ChatMessageListProps {
  messages: ChatMessageView[];
  emptyMessage: string;
  formatTime: (createdAt: string) => string;
  ariaLabel: string;
}

/** A live, auto-scrolling transcript with familiar left/right message bubbles. */
export function ChatMessageList({
  messages,
  emptyMessage,
  formatTime,
  ariaLabel,
}: ChatMessageListProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const latestMessageId = messages.at(-1)?.id;

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [latestMessageId]);

  return (
    <Box
      role="log"
      aria-label={ariaLabel}
      aria-live="polite"
      aria-relevant="additions"
      sx={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        flexDirection: "column",
        gap: 1.25,
        paddingBlock: 2,
        paddingInline: { xs: 2, sm: 2.5 },
        overscrollBehavior: "contain",
      }}
    >
      {messages.length === 0 ? (
        <Box
          sx={{
            display: "grid",
            flex: 1,
            minHeight: 180,
            placeItems: "center",
            textAlign: "center",
            padding: 3,
          }}
        >
          <Typography variant="T2" sx={{ color: "text.muted", maxWidth: 360 }}>
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: message.isOwn ? "flex-end" : "flex-start",
              alignSelf: message.isOwn ? "flex-end" : "flex-start",
              maxWidth: { xs: "88%", sm: "78%" },
            }}
          >
            {message.senderLabel ? (
              <Typography
                variant="L3"
                sx={{
                  color: "text.muted",
                  paddingInline: 1,
                  marginBottom: 0.375,
                }}
              >
                {message.senderLabel}
              </Typography>
            ) : null}

            <Box
              sx={{
                borderRadius: message.isOwn
                  ? `${RADIUS.lg}px ${RADIUS.lg}px ${RADIUS.sm}px ${RADIUS.lg}px`
                  : `${RADIUS.lg}px ${RADIUS.lg}px ${RADIUS.lg}px ${RADIUS.sm}px`,
                backgroundColor: message.isOwn
                  ? "primary.main"
                  : "background.default",
                color: message.isOwn ? "primary.contrastText" : "text.primary",
                border: "1px solid",
                borderColor: message.isOwn ? "primary.main" : "divider",
                paddingBlock: 1,
                paddingInline: 1.5,
              }}
            >
              <Typography
                variant="T2"
                component="p"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {message.body}
              </Typography>
            </Box>

            <Typography
              variant="L3"
              component="time"
              dateTime={message.createdAt}
              sx={{ color: "text.muted", paddingInline: 1, marginTop: 0.375 }}
            >
              {formatTime(message.createdAt)}
            </Typography>
          </Box>
        ))
      )}
      <Box ref={endRef} aria-hidden />
    </Box>
  );
}
